import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Calendar, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, 
  isSameDay, isWithinInterval, isSameMonth, 
  addDays, addWeeks, addMonths, subDays, subWeeks, subMonths,
  startOfDay, endOfDay
} from 'date-fns';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import AppointmentCard from './AppointmentCard';
import { AppointmentDetails } from './AppointmentDetails';
import { BookAppointment } from './BookAppointment';

export const Appointments = () => {
  const navigate = useNavigate();
  const { get, post, put } = useApi();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // New State for View Switching
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

   const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await get('/appointments');
      
      if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
        setAppointments(response.data.data);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const response = await put(`/appointments/${appointmentId}/status`, {
        status: newStatus
      });
      
      if (response.data?.status === 'success') {
        if (newStatus === 'in-progress') {

            navigate(`/counselor/sessions/${appointmentId}`);
          
        } else {
          fetchAppointments();
        }
      }
    } catch (error) {
      console.error('Failed to update appointment status:', error);
    }
  };
  const navigateDate = (direction) => {
    const amount = direction === 'next' ? 1 : -1;
    if (viewMode === 'day') setCurrentDate(prev => addDays(prev, amount));
    if (viewMode === 'week') setCurrentDate(prev => addWeeks(prev, amount));
    if (viewMode === 'month') setCurrentDate(prev => addMonths(prev, amount));
  };

  const getDateRangeLabel = () => {
    if (viewMode === 'day') return format(currentDate, 'MMMM d, yyyy');
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
    if (viewMode === 'month') return format(currentDate, 'MMMM yyyy');
  };

  const filteredAppointments = appointments.filter(appointment => {
    if (!appointment) return false;
    
    // Date Filtering
    const appointmentDate = new Date(appointment.date);
    let matchesDate = false;

    if (viewMode === 'day') {
      matchesDate = isSameDay(appointmentDate, currentDate);
    } else if (viewMode === 'week') {
      matchesDate = isWithinInterval(appointmentDate, {
        start: startOfWeek(currentDate),
        end: endOfWeek(currentDate)
      });
    } else if (viewMode === 'month') {
      matchesDate = isSameMonth(appointmentDate, currentDate);
    }

    if (!matchesDate) return false;

    const studentId = appointment.client?.studentInfo?.studentId;
    const staffId = appointment.client?.staffInfo?.staffId;
    
    const matchesFilter = filter === 'all' || appointment.status === filter;
    
    if (!searchQuery.trim()) return matchesFilter;

    const searchLower = searchQuery.toLowerCase().trim();
    
    // Get search fields
    const clientName = appointment.client?.username?.toLowerCase() || '';
    const counselorName = appointment.counselor?.username?.toLowerCase() || '';
    const studentIdLower = studentId?.toString().toLowerCase() || '';
    const staffIdLower = staffId?.toString().toLowerCase() || '';
    
    const matchesSearch = 
      clientName.includes(searchLower) ||
      counselorName.includes(searchLower) ||
      studentIdLower.includes(searchLower) ||
      staffIdLower.includes(searchLower);

    return matchesFilter && matchesSearch;
  });


  const canBookAppointments = ['student', 'receptionist', 'staff'].includes(user?.role);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      );
    }

    if (!appointments.length) {
      return (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">No appointments found</h3>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            {canBookAppointments ? 'Click "Book Appointment" to get started' : 'No appointments available at this time'}
          </p>
        </div>
      );
    }

    if (!filteredAppointments.length) {
      return (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">No matching appointments</h3>
          <p className="mt-1 text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAppointments.map(appointment => (
          <AppointmentCard
            key={appointment._id}
            appointment={appointment}
            onViewDetails={() => setSelectedAppointmentId(appointment._id)}
            onStatusChange={(status) => handleStatusChange(appointment._id, status)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Appointments</h1>
        <div className="flex items-center space-x-4">
          {/* View Switcher */}
          <div className="flex bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
            {['day', 'week', 'month'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === mode
                    ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          
          {canBookAppointments && (
            <button
              onClick={() => setShowBooking(true)}
              className="flex items-center px-4 py-2 text-sm sm:text-base sm:px-4 sm:py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
            >
              <Plus className="h-4 w-4 mr-2 sm:h-5 sm:w-5" />
              Book Appointment
            </button>
          )}
        </div>
      </div>

      {/* Date Navigation */}
      <div className="mb-6 flex items-center justify-between bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigateDate('prev')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-300"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-lg font-semibold text-gray-900 dark:text-white min-w-[200px] text-center">
            {getDateRangeLabel()}
          </span>
          <button 
            onClick={() => navigateDate('next')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-300"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline px-2"
          >
            Today
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search appointments..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              className="px-3 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no-show">No Show</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {renderContent()}

      {/* Modals */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <BookAppointment
              onClose={() => {
                setShowBooking(false);
                fetchAppointments();
              }}
            />
          </div>
        </div>
      )}

      {selectedAppointmentId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg max-w-2xl w-full">
            <AppointmentDetails
              appointmentId={selectedAppointmentId}
              onClose={() => {
                setSelectedAppointmentId(null);
                fetchAppointments();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;